import type { Translations } from '@vp/swan'

const cache = new Map<string, Promise<Translations>>()

/**
 * Cached dynamic import that ensures each URL is only imported once.
 * Subsequent calls for the same URL return the cached Promise.
 *
 * If an import fails, the cache entry is removed to allow retry on subsequent calls.
 *
 * @param url - The module URL to import (e.g., '@vp/swan/i18n/en-us.json')
 * @returns Promise that resolves to the imported module (default export or full module)
 *
 * @example
 * const translations = await jsonImport('@vp/swan/i18n/en-us.json')
 */
export async function jsonImport<T>(url: string): Promise<T> {
  if (!cache.has(url)) {
    const importPromise = import(url)
      .then((imp) => imp.default || imp)
      .catch((error) => {
        // Remove failed import from cache to allow retry
        console.log(`[ubik-fragment-react] Failed to import json module at ${url}:`, error)
        cache.delete(url)
        throw error
      })

    cache.set(url, importPromise)
  }

  return cache.get(url) as Promise<T>
}

/**
 * Clear the cache for a specific URL or all URLs.
 * Useful for testing or when modules need to be reloaded.
 *
 * @param url - Optional URL to clear. If omitted, clears entire cache.
 */
export function clearJsonCache(url?: string): void {
  if (url) {
    cache.delete(url)
  } else {
    cache.clear()
  }
}
